import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Paper } from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Button from '@material-ui/core/Button';
import { useHistory } from 'react-router-dom';
import { getCurrentUser } from '../store/actions/auth';
import { getAdmin } from '../store/actions/admin';
import { MENU_WIDTH } from '../constants';
import InviteModal from '../components/InviteModal';
import SupervisorCell from '../components/SupervisorCell';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    paddingLeft: MENU_WIDTH,
    paddingTop: '6rem',
    height: '100%',
  },
  headerRow: {
    display: 'flex',
    flexDirection: 'row',
    marginLeft: '2rem',
    marginTop: '2rem',
    justifyContent: 'flex-end',
    marginRight: '2rem',
  },
  row: {
    height: 81,
  },
  button: {
    color: 'white',
    backgroundColor: '#00d1b2',
    '&:hover': {
      backgroundColor: '#00bca0',
    },
  },
  paper: {
    margin: '2em',
  },
  content: {
    flexGrow: 1,
    borderRadius: 4,
  },
  table: {
    width: '100%',
  },
  headerCell: {
    backgroundColor: '#526488',
    fontWeight: 'bold',
    textAlign: 'center',
    color: 'white',
  },
}));

const Admin = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const currentUser = useSelector((state) => state.admin.currentUserInfo);
  const usersList = useSelector((state) => state.admin.usersList);
  const [displayList, setDisplayList] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isInviteModalOpen, setInviteModalOpen] = useState(false);
  const classes = useStyles();
  const [isSupervisor, setIsSupervisor] = useState(false);

  useEffect(() => {
    dispatch(getCurrentUser());
    dispatch(getAdmin());
  }, []);

  useEffect(() => {
    if (currentUser && currentUser.role && currentUser.role.name === 'Supervisor') setIsSupervisor(true);
    else setIsSupervisor(false);
  }, [currentUser]);

  const columns = [
    {
      id: 'name',
      label: 'Name',
      minWidth: 170,
      align: 'center',
    },
    {
      id: 'email',
      label: 'Email',
      minWidth: 170,
      align: 'center',
    },
    {
      id: 'phone',
      label: 'Phone Number',
      minWidth: 170,
      align: 'center',
    },
    {
      id: 'supervisor',
      label: 'Supervisor',
      minWidth: 170,
      align: 'center',
    },
    {
      id: 'patients',
      label: 'Patients',
      minWidth: 170,
      align: 'left',
    },
    {
      id: 'show_notifications',
      label: 'Notification Log',
      minWidth: 70,
      align: 'center',
    },  
  ];

  useEffect(() => {
    setDisplayList(
      usersList.map((user) => {
        let patientList = '';
        user.patients.forEach((patient, index) => {
          if (index) patientList = `${patientList}, `;
          patientList = `${patientList}${patient.first_name} ${patient.last_name}`;
        });
        return {
          user,
          name: `${user.first_name} ${user.last_name}`,
          phone: user.phone_number,
          email: user.email,
          patients: patientList,
          supervisor: user.supervisor ? `${user.supervisor.first_name} ${user.supervisor.last_name}` : null,
          id: user.id,
          show_notifications: 
          <Button className={classes.button} size="small" 
          variant="contained" 
          onClick={() => handleNavigation(user.id, "alerts")}>Notification Log</Button>,  

        };
      }),
    );
  }, [usersList]);

  const handleNavigation = (id) => {
    history.push(`./notification-log/${id}`);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return (
    <div className={classes.root}>
      <InviteModal open={isInviteModalOpen} setOpen={setInviteModalOpen} />
      <div className={classes.headerRow}>
        <Button className={classes.button} variant="contained" onClick={() => setInviteModalOpen(true)}>
          Invite New User
        </Button>
      </div>
      <Paper className={classes.paper}>
        <TableContainer className={classes.content}>
          <Table stickyHeader aria-label="sticky table" className={classes.table}>
            <TableHead>
              <TableRow>
                {columns.map((column, index) => (
                  <TableCell
                    className={classes.headerCell}
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => {
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow className={classes.row} hover key={index}>
                    {columns.map((column) => {
                      const value = row[column.id];
                      return isSupervisor && column.id === 'supervisor' ? (
                        <SupervisorCell key={column.id} users={usersList} column={column} rowValue={row} />
                      ) : (
                        <TableCell key={column.id} align={column.align} className={classes.cell}>
                          {column.format ? column.format(value) : value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={displayList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
};

export default Admin;
